import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingDataLayerProduct } from '@makro-shopping/use-product-card-logic/dist/types/product.type';
import { useStaticData } from '../contexts/DataProvider.context';
import SendBrowserEvent from '../services/sendBrowserEvent.service';
import { Product, ProductSearchResult } from '../types/product.types';

const BOTTOM_SHEET_SWIPE_DISMISS_THRESHOLD = 80;

export const useAskToLogin = ({
  product,
  promotionData,
  isFromBasket = false,
  isHorizonUiUxEnabled = false,
  onModalOpen,
  onModalClose,
}: {
  product: ProductSearchResult | Product | ShoppingDataLayerProduct;
  promotionData?: {
    collectionId?: string;
    collectionHandle?: string;
    isComplexPromotion?: boolean;
    productId?: string;
  };
  isFromBasket?: boolean;
  isHorizonUiUxEnabled?: boolean;
  onModalOpen?: () => void;
  onModalClose?: () => void;
}) => {
  const { userDetails } = useStaticData();
  const isLoggedIn = !!userDetails?.token;
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);

  const onClose = useCallback(() => {
    setOpenModal(false);
    onModalClose?.();

    if (isFromBasket && isHorizonUiUxEnabled) {
      SendBrowserEvent.horizonBottomSheetState(false, true);
      return;
    }

    // The Horizon carousel restores its measured pagination height through
    // onModalClose. Non-Horizon carousels use the existing iframe handshake.
    if (!isHorizonUiUxEnabled) {
      SendBrowserEvent.setIframeHeight(false, false);
    }
  }, [isFromBasket, isHorizonUiUxEnabled, onModalClose]);

  const onOpen = useCallback(() => {
    setOpenModal(true);
    onModalOpen?.();
    if (isFromBasket && isHorizonUiUxEnabled) {
      SendBrowserEvent.horizonBottomSheetState(true, true);
      return;
    }

    SendBrowserEvent.setIframeHeight(isHorizonUiUxEnabled, true);
  }, [isFromBasket, isHorizonUiUxEnabled, onModalOpen]);

  useEffect(() => {
    if (!openModal || !isFromBasket || !isHorizonUiUxEnabled) return;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverscrollBehaviorY = html.style.overscrollBehaviorY;
    const previousBodyOverscrollBehaviorY = body.style.overscrollBehaviorY;
    let touchStart: {x: number; y: number} | null = null;

    // Keep a swipe that starts inside the iframe from chaining to the basket's
    // scroll container. Set both because the iframe document has both roots.
    html.style.overscrollBehaviorY = 'contain';
    body.style.overscrollBehaviorY = 'contain';

    const handleTouchStart = (event: TouchEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest?.('.MuiDialog-paper') || event.touches.length !== 1) {
        touchStart = null;
        return;
      }

      touchStart = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!touchStart || event.touches.length !== 1) return;

      const deltaY = event.touches[0].clientY - touchStart.y;
      const deltaX = event.touches[0].clientX - touchStart.x;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (!touchStart || event.changedTouches.length !== 1) return;

      const deltaY = event.changedTouches[0].clientY - touchStart.y;
      const deltaX = event.changedTouches[0].clientX - touchStart.x;
      touchStart = null;

      if (
        deltaY >= BOTTOM_SHEET_SWIPE_DISMISS_THRESHOLD &&
        Math.abs(deltaY) > Math.abs(deltaX) * 1.2
      ) {
        onClose();
      }
    };

    document.addEventListener('touchstart', handleTouchStart, {capture: true, passive: true});
    document.addEventListener('touchmove', handleTouchMove, {capture: true, passive: false});
    document.addEventListener('touchend', handleTouchEnd, {capture: true, passive: true});

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, true);
      document.removeEventListener('touchmove', handleTouchMove, true);
      document.removeEventListener('touchend', handleTouchEnd, true);
      html.style.overscrollBehaviorY = previousHtmlOverscrollBehaviorY;
      body.style.overscrollBehaviorY = previousBodyOverscrollBehaviorY;
    };
  }, [openModal, isFromBasket, isHorizonUiUxEnabled, onClose]);

  const redirectToApplyCardPage = useCallback(() => {
    onClose();
    SendBrowserEvent.navigateToApplyCard();
  }, [onClose]);

  const redirectToPromotionPlp = useCallback(() => {
    onClose();
    // Send prioritized promotion data instead of just product
    SendBrowserEvent.navigateToPromotionPlp(product, promotionData);
  }, [onClose, product, promotionData]);

  const title = !isLoggedIn ? t('askToLogin.loginLabel') : t('askToLogin.applyCardLabel');
  const desc = !isLoggedIn ? t('askToLogin.loginDesc') : t('askToLogin.applyCardDesc');
  const buttonLabel = !isLoggedIn ? t('askToLogin.loginBtn') : t('askToLogin.applyCardBtn');

  const askToLoginProps = useMemo(
    () => ({
      openModal,
      onOpen,
      onClose,
      redirectToApplyCardPage,
      redirectToPromotionPlp,
    }),
    [openModal, onOpen, onClose, redirectToApplyCardPage, redirectToPromotionPlp],
  );

  return {
    openModal,
    onClose,
    onOpen,
    loginOrApplyTitle: title,
    loginOrApplyDesc: desc,
    loginOrApplyButtonText: buttonLabel,
    askToLoginProps,
  };
};
